import React from 'react';
import { useImmer } from 'use-immer';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { GET_EQUALIZATION_STORAGE_PER_ERC, SET_EQUALIZATION_STORAGE_PER_ERC } from '../../../../../queries/engineeringQrys';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { numbersUpToTwoDecimalPlaces } from '../../../../reuseableComponents/forms/formValidation';
import { formatNumber } from '../../dwriUtils';
import '../../dwri.css';

const INITIAL_FORM_STATE = {
  adjustedEqualizationStorageErcValue: '',
  adjustedEqualizationStorageErcError: '',
  changeJustificationValue: '',
  changeJustificationError: '',
};

const DwriEqualizationStoragePerErc = ({ pwsId, years, selectedSources, onSaved }) => {

  const [editRow, setEditRow] = React.useState(null);
  const [formState, setFormState] = useImmer(INITIAL_FORM_STATE);
  const [mutationError, setMutationError] = React.useState('');

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : { isPublic: true };
  const canEditWaterUseSystem = user?.permissions?.includes('edit_water_use_system');

  const [setEqualizationStorage] = useMutation(SET_EQUALIZATION_STORAGE_PER_ERC);

  const { data, loading, error, refetch } = useQuery(GET_EQUALIZATION_STORAGE_PER_ERC, {
    variables: { pwsId, years, sources: selectedSources },
    skip: !pwsId || !years || years.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const rows = (data?.getEqualizationStoragePerErc || []).map((row, index) => ({
    key: `${row.year}-${index}`,
    year: row.year,
    averageAnnualDemandErc: row.averageAnnualDemandErc,
    operationalDays: row.operationalDays,
    equalizationStoragePerErcGallons: row.equalizationStoragePerErcGallons,
    adjustedEqualizationStorageErc: row.adjustedEqualizationStorageErc,
    changeJustification: row.changeJustification,
  }));

  if (error) return <p className="text-danger">Error loading Equalization Storage per ERC</p>;
  if (loading) return <p>Loading...</p>;

  function handleEditClick(row) {
    setEditRow(row);
    setFormState({
      ...INITIAL_FORM_STATE,
      adjustedEqualizationStorageErcValue: row.adjustedEqualizationStorageErc == null ? '' : String(row.adjustedEqualizationStorageErc),
      changeJustificationValue: row.changeJustification ?? '',
    });
    setMutationError('');
  }

  function isAdjustedEqualizationStorageErcChanged() {
    if (!editRow) {
      return false;
    }

    const previousValue = editRow.adjustedEqualizationStorageErc == null
      ? ''
      : String(editRow.adjustedEqualizationStorageErc);

    return formState.adjustedEqualizationStorageErcValue !== previousValue;
  }

  async function handleSave() {
    const nextAdjustedErcError = formState.adjustedEqualizationStorageErcValue.trim()
      ? (numbersUpToTwoDecimalPlaces(formState.adjustedEqualizationStorageErcValue) === true
          ? ''
          : numbersUpToTwoDecimalPlaces(formState.adjustedEqualizationStorageErcValue))
      : '';

    const nextChangeJustificationError = (formState.adjustedEqualizationStorageErcValue.trim() !== '' && !formState.changeJustificationValue.trim())
      ? 'Change Justification is required.'
      : '';

    setFormState((draft) => {
      draft.adjustedEqualizationStorageErcError = nextAdjustedErcError;
      draft.changeJustificationError = nextChangeJustificationError;
    });

    if (nextAdjustedErcError || nextChangeJustificationError) {
      return;
    }

    setMutationError('');
    try {
      const response = await setEqualizationStorage({
        variables: {
          updateEqualizationStorageInput: {
            pwsId: pwsId,
            year: editRow.year,
            adjustedEqualizationStorageErc: formState.adjustedEqualizationStorageErcValue === ''
              ? null
              : parseFloat(formState.adjustedEqualizationStorageErcValue),
            changeJustification: formState.changeJustificationValue,
          }
        },
        errorPolicy: 'all',
      });

      const responseErrorMessage = response?.errors?.[0]?.message;
      if (responseErrorMessage) {
        setMutationError(responseErrorMessage);
        return;
      }

      if (!response?.data?.updateEqualizationStoragePerErc) {
        setMutationError('Failed to update equalization storage per ERC.');
        return;
      }

      refetch();
      onSaved?.();
      handleModalClose();
    } catch (err) {
      setMutationError(err?.message || 'Failed to update equalization storage per ERC.');
    }
  }

  function handleModalClose() {
    setEditRow(null);
    setFormState(INITIAL_FORM_STATE);
    setMutationError('');
  }

  function setFormValue(field, value) {
    setFormState((draft) => {
      draft[field] = value;
      if (field === 'adjustedEqualizationStorageErcValue') {
        draft.adjustedEqualizationStorageErcError = '';
      }
      if (field === 'changeJustificationValue') {
        draft.changeJustificationError = '';
      }
    });
  }

  function handleNumericFieldChange(valueField, errorField, event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);

    setFormState((draft) => {
      draft[valueField] = nextValue;
      draft[errorField] = validationResult === true ? '' : validationResult;
      if (valueField === 'adjustedEqualizationStorageErcValue' && nextValue.trim() === '') {
        draft.changeJustificationError = '';
      }
    });
  }

  function handleNumericFieldBlur(valueField, errorField, event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    setFormState((draft) => {
      draft[valueField] = nextValue;
      draft[errorField] = validationResult === true ? '' : validationResult;
    });
  }

  return (
    <>
      <Row>
        <Col><span><b>Equalization Storage per ERC (GAL)</b></span></Col>
      </Row>
      <div className="table-responsive">
        <Row className="dwri-service-connections-header gx-0">
          <Col className="dwri-table-header-cell dwri-col-year"><span><b>Year</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Average Annual Demand ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Operational Days</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Equalization Storage ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Adjusted Equalization Storage ERC</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Change Justification</b></span></Col>
          {canEditWaterUseSystem && (
            <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
          )}
        </Row>
        {rows.map((row, idx) => (
          <Row key={row.key} className={`dwri-service-connections-row gx-0 ${idx % 2 === 0 ? 'is-even' : 'is-odd'}`}>
            <Col className="dwri-table-cell dwri-col-year"><span>{row.year}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.averageAnnualDemandErc)}</span></Col>
            <Col className="dwri-table-cell"><span>{row.operationalDays}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.equalizationStoragePerErcGallons)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.adjustedEqualizationStorageErc)}</span></Col>
            <Col className="dwri-table-cell"><span>{row.changeJustification}</span></Col>
            {canEditWaterUseSystem && (
            <Col className="dwri-table-cell dwri-col-edit">
              <button
                className="link-button"
                aria-label={`Edit equalization storage for year ${row.year}`}
                type="button"
                onClick={() => handleEditClick(row)}
              >
                Edit
              </button>
            </Col>
            )}
          </Row>
        ))}
      </div>
      {editRow && (
            <Modal
              onClose={handleModalClose}
              onEscape={handleModalClose}
            >
              <ModalTitle>Update Equalization Storage per ERC</ModalTitle>
              <ModalContent>
                <div className="dwri-peak-day-demand-modal-grid">
                  <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                    <TextInput label="Year" value={String(editRow.year ?? '')} isDisabled />
                    <TextInput label="Average Annual Demand per ERC" value={formatNumber(editRow.averageAnnualDemandErc ?? '')} isDisabled />
                  </div>
                  <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                    <TextInput label="Operational Days" value={editRow.operationalDays ?? ''} isDisabled />
                    <TextInput label="Equalization Storage per ERC" value={formatNumber(editRow.equalizationStorageErc ?? '')} isDisabled />
                  </div>
                  <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                    <TextInput 
                      label="Adjusted Equalization Storage ERC" 
                      value={formState.adjustedEqualizationStorageErcValue}
                      onChange={(event) => handleNumericFieldChange('adjustedEqualizationStorageErcValue', 'adjustedEqualizationStorageErcError', event)}
                      onBlur={(event) => handleNumericFieldBlur('adjustedEqualizationStorageErcValue', 'adjustedEqualizationStorageErcError', event)}
                      errorMessage={formState.adjustedEqualizationStorageErcError || undefined}
                    />
                  </div>
                  <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--full">
                    <TextArea
                      label="Change Justification"
                      value={formState.changeJustificationValue}
                      onChange={(event) => setFormValue('changeJustificationValue', event.target.value)}
                      errorMessage={formState.changeJustificationError || undefined}
                      isRequired={isAdjustedEqualizationStorageErcChanged()}
                    />
                  </div>
                </div>
              </ModalContent>
              <ModalFooter className="dwri-service-modal-footer">
                {mutationError && (
                  <div className="text-danger">{mutationError}</div>
                )}
                <button type="button" onClick={handleModalClose}>Close</button>
                <button type="button" onClick={handleSave}>Save</button>
              </ModalFooter>
            </Modal>
      )}
    </>
  );

}

export default DwriEqualizationStoragePerErc;